import { Badge, Box, Group, JsonInput, Modal, ScrollArea, Select, Stack, Table, Tabs, Text, Timeline, Title } from '@mantine/core';
import { IconActivity, IconDatabase, IconHistory, IconInfoCircle, IconNetwork } from '@tabler/icons-react';
import React, { Suspense, useCallback, useMemo, useState } from 'react';

type RowLike = { id: string; key: string; type?: string; name?: string; draft?: Record<string, unknown> };

interface RelationshipNode { id: string; key: string; kind: string; name?: string; draft?: any }
interface RelationshipEdge { from: string; to: string; reason: string }

export interface RelationshipModalProps {
  tenant: string;
  opened: boolean;
  onClose: () => void;
  selected: RowLike | null;
  rows: RowLike[];
  onHydrateAll?: () => Promise<void>;
}

function safeJSON(obj: unknown): string { try { return JSON.stringify(obj ?? {}); } catch { return '{}'; } }
function includesToken(haystack: string, token: string | undefined): boolean { if (!token) return false; try { return haystack.toLowerCase().includes(String(token).toLowerCase()); } catch { return false; } }

function resolveRelationships(selected: RelationshipNode, nodes: RelationshipNode[]): RelationshipEdge[] {
  const edges: RelationshipEdge[] = [];
  const selBlob = safeJSON(selected.draft);
  const selKey = selected.key;
  const selName = selected.name;
  for (const other of nodes) {
    if (other.id === selected.id) continue;
    const otherBlob = safeJSON(other.draft);
    if (includesToken(otherBlob, selKey) || includesToken(otherBlob, selName)) {
      edges.push({ from: other.id, to: selected.id, reason: `references ${selected.kind}` });
    }
    if (includesToken(selBlob, other.key) || includesToken(selBlob, other.name)) {
      edges.push({ from: selected.id, to: other.id, reason: `references ${other.kind}` });
    }
    if ((selected.kind === 'Instance') && (other.kind === 'WorkflowDefinition')) {
      if (includesToken(selBlob, other.key) || includesToken(selBlob, other.name) || includesToken(otherBlob, selName)) {
        edges.push({ from: selected.id, to: other.id, reason: 'instance_of' });
      }
    }
    if ((selected.kind === 'WorkflowDefinition') && (other.kind === 'Instance')) {
      if (includesToken(otherBlob, selected.key) || includesToken(otherBlob, selected.name) || includesToken(selBlob, other.name)) {
        edges.push({ from: other.id, to: selected.id, reason: 'instance_of' });
      }
    }
    if (selected.kind === 'WorkflowDefinition' && other.kind === 'WorkflowDefinition') {
      if (includesToken(selBlob, 'invoke') && (includesToken(selBlob, other.key) || includesToken(selBlob, other.name))) {
        edges.push({ from: selected.id, to: other.id, reason: 'invokes' });
      }
    }
  }
  const seen = new Set<string>();
  return edges.filter(e => { const k = `${e.from}|${e.to}|${e.reason}`; if (seen.has(k)) return false; seen.add(k); return true; });
}

export const RelationshipModal: React.FC<RelationshipModalProps> = ({ tenant, opened, onClose, selected, rows, onHydrateAll }) => {
  const [activeTab, setActiveTab] = useState<string>('details');
  const [view, setView] = useState<'json' | 'cytoscape'>('json');
  const [hydrating, setHydrating] = useState(false);

  // Cytoscape fallback - disabled for now to avoid build errors
  const cyReady = false;
  const CytoscapeComponent: React.ComponentType<any> | null = null;
  const cyElements: any[] = [];

  const nodes = useMemo<RelationshipNode[]>(() => rows.map(r => ({ id: r.id, key: r.key, kind: r.type || 'Unknown', name: r.name || r.key, draft: r.draft || {} })), [rows]);
  const selectedNode = useMemo<RelationshipNode | null>(() => selected ? { id: selected.id, key: selected.key, kind: selected.type || 'Unknown', name: selected.name || selected.key, draft: selected.draft || {} } : null, [selected]);
  const edges = useMemo<RelationshipEdge[]>(() => selectedNode ? resolveRelationships(selectedNode, nodes) : [], [selectedNode, nodes]);

  // Find workflow instances (for WorkflowDefinition entities)
  const workflowInstances = useMemo(() => {
    if (!selectedNode || selectedNode.kind !== 'WorkflowDefinition') return [];
    return nodes.filter(n =>
      n.kind === 'Instance' &&
      edges.some(e => e.from === n.id && e.to === selectedNode.id && e.reason === 'instance_of')
    );
  }, [selectedNode, nodes, edges]);

  // Mock history data (in real implementation, this would come from GraphQL audit trail query)
  const historyEvents = useMemo(() => {
    if (!selected?.draft) return [];

    const draft = selected.draft as any;
    const events = [];

    // Extract creation info
    if (draft.createdAt || draft.created_at) {
      events.push({
        timestamp: draft.createdAt || draft.created_at,
        action: 'Created',
        userId: draft.createdBy || draft.created_by || 'system',
        details: `${selected.type} created`
      });
    }

    // Extract update info
    if (draft.updatedAt || draft.updated_at) {
      events.push({
        timestamp: draft.updatedAt || draft.updated_at,
        action: 'Updated',
        userId: draft.updatedBy || draft.updated_by || 'system',
        details: 'Entity metadata updated'
      });
    }

    // Extract status changes
    if (draft.status) {
      events.push({
        timestamp: draft.updatedAt || draft.updated_at || Date.now(),
        action: 'Status Changed',
        userId: 'system',
        details: `Status set to ${draft.status}`
      });
    }

    return events.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  }, [selected]);

  const hydrate = useCallback(async () => { if (!onHydrateAll) return; setHydrating(true); try { await onHydrateAll(); } finally { setHydrating(false); } }, [onHydrateAll]);

  // Optional Cytoscape (dynamic)
  // Cytoscape view intentionally disabled unless explicitly integrated as a separate bundle

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group>
          <Title order={5}>{selected?.name || selected?.key || 'Entity Details'}</Title>
          {selected && <Badge color="blue">{selected.type}</Badge>}
        </Group>
      }
      size="xl"
      centered
    >
      {!selected && <Text c="dimmed" size="sm">Select a row to view details and relationships.</Text>}
      {selected && (
        <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'details')}>
          <Tabs.List>
            <Tabs.Tab value="details" leftSection={<IconInfoCircle size={16} />}>
              Details
            </Tabs.Tab>
            <Tabs.Tab value="instances" leftSection={<IconActivity size={16} />}>
              Instances ({workflowInstances.length})
            </Tabs.Tab>
            <Tabs.Tab value="relationships" leftSection={<IconNetwork size={16} />}>
              Relationships ({edges.length})
            </Tabs.Tab>
            <Tabs.Tab value="history" leftSection={<IconHistory size={16} />}>
              History ({historyEvents.length})
            </Tabs.Tab>
          </Tabs.List>

          {/* Tab 1: Details - Show entity metadata */}
          <Tabs.Panel value="details">
            <ScrollArea h={450} mt="md">
              <Stack gap="md">
                <Group>
                  <Text size="sm" fw={600} w={120}>ID:</Text>
                  <Text size="sm" c="dimmed">{selected.id}</Text>
                </Group>
                <Group>
                  <Text size="sm" fw={600} w={120}>Key:</Text>
                  <Text size="sm" c="dimmed">{selected.key}</Text>
                </Group>
                <Group>
                  <Text size="sm" fw={600} w={120}>Type:</Text>
                  <Badge size="sm">{selected.type || 'Unknown'}</Badge>
                </Group>
                <Group>
                  <Text size="sm" fw={600} w={120}>Name:</Text>
                  <Text size="sm">{selected.name || '—'}</Text>
                </Group>

                {/* Display all draft metadata */}
                {selected.draft && Object.keys(selected.draft).length > 0 && (
                  <>
                    <Title order={6} mt="md">Entity Metadata</Title>
                    <JsonInput
                      value={JSON.stringify(selected.draft, null, 2)}
                      readOnly
                      minRows={10}
                      maxRows={20}
                      styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
                    />
                  </>
                )}
              </Stack>
            </ScrollArea>
          </Tabs.Panel>

          {/* Tab 2: Instances - Show workflow instances (for WorkflowDefinition) */}
          <Tabs.Panel value="instances">
            <ScrollArea h={450} mt="md">
              {selectedNode?.kind === 'WorkflowDefinition' ? (
                workflowInstances.length > 0 ? (
                  <Table striped highlightOnHover withTableBorder withColumnBorders>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Instance ID</Table.Th>
                        <Table.Th>Name</Table.Th>
                        <Table.Th>Status</Table.Th>
                        <Table.Th>Created</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {workflowInstances.map((instance) => {
                        const draft = instance.draft as any;
                        return (
                          <Table.Tr key={instance.id}>
                            <Table.Td>{instance.id}</Table.Td>
                            <Table.Td>{instance.name}</Table.Td>
                            <Table.Td>
                              <Badge size="sm" color={draft?.status === 'running' ? 'green' : 'gray'}>
                                {draft?.status || 'Unknown'}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              {draft?.createdAt
                                ? new Date(draft.createdAt).toLocaleString()
                                : '—'}
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                ) : (
                  <Text c="dimmed" size="sm">No workflow instances found for this definition.</Text>
                )
              ) : (
                <Text c="dimmed" size="sm">
                  Instances are only shown for WorkflowDefinition entities. This is a {selectedNode?.kind}.
                </Text>
              )}
            </ScrollArea>
          </Tabs.Panel>

          {/* Tab 3: Relationships - Show relationship graph */}
          <Tabs.Panel value="relationships">
            <Stack mt="md">
              <Group justify="space-between">
                <Text size="sm">Tenant: {tenant}</Text>
                <Group>
                  <Select
                    value={view}
                    onChange={(v: string | null) => setView((v as 'json' | 'cytoscape') || 'json')}
                    w={160}
                    data={[
                      { value: 'json', label: 'Table View' },
                      { value: 'cytoscape', label: 'Graph View' }
                    ]}
                  />
                </Group>
              </Group>

              {view === 'json' || !cyReady ? (
                <ScrollArea h={380}>
                  <Table striped highlightOnHover withTableBorder withColumnBorders>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>From</Table.Th>
                        <Table.Th>→</Table.Th>
                        <Table.Th>To</Table.Th>
                        <Table.Th>Reason</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {edges.length === 0 && (
                        <Table.Tr>
                          <Table.Td colSpan={4}>
                            <Text c="dimmed">No relationships found. Try Hydrate.</Text>
                          </Table.Td>
                        </Table.Tr>
                      )}
                      {edges.map((e, i) => {
                        const from = nodes.find((n) => n.id === e.from);
                        const to = nodes.find((n) => n.id === e.to);
                        return (
                          <Table.Tr key={`${e.from}-${e.to}-${i}`}>
                            <Table.Td>
                              {from?.kind}:{from?.name}
                            </Table.Td>
                            <Table.Td>→</Table.Td>
                            <Table.Td>
                              {to?.kind}:{to?.name}
                            </Table.Td>
                            <Table.Td>{e.reason}</Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                  <Group justify="flex-end" mt="xs">
                    <Text size="xs" c="dimmed">
                      {hydrating ? 'Hydrating…' : ''}
                    </Text>
                  </Group>
                </ScrollArea>
              ) : CytoscapeComponent ? (
                <Box style={{ height: 420 }}>
                  <Suspense fallback={<Text size="sm">Loading Cytoscape…</Text>}>
                    {React.createElement(CytoscapeComponent as React.ComponentType<any>, {
                      elements: cyElements as any,
                      style: { width: '100%', height: '100%' },
                      layout: { name: 'cose' },
                      stylesheet: [
                        {
                          selector: 'node',
                          style: {
                            label: 'data(label)',
                            'font-size': 10,
                            'text-wrap': 'wrap',
                            'text-max-width': 180
                          }
                        },
                        {
                          selector: 'edge',
                          style: {
                            'curve-style': 'bezier',
                            'target-arrow-shape': 'triangle',
                            label: 'data(label)',
                            'font-size': 8
                          }
                        }
                      ]
                    })}
                  </Suspense>
                </Box>
              ) : null}

              <Group justify="space-between">
                <Text size="xs" c="dimmed">
                  Selected: {selected.type}:{selected.name || selected.key}
                </Text>
                <Group>
                  <Text size="xs" c="dimmed">
                    Edges: {edges.length}
                  </Text>
                </Group>
              </Group>

              <Group justify="flex-end">
                <Box>
                  <a
                    onClick={(e) => {
                      e.preventDefault();
                      hydrate();
                    }}
                    style={{ cursor: 'pointer', fontSize: 12 }}
                  >
                    {hydrating ? 'Hydrating…' : 'Hydrate drafts for better detection'}
                  </a>
                </Box>
              </Group>
            </Stack>
          </Tabs.Panel>

          {/* Tab 4: History - Show audit trail timeline */}
          <Tabs.Panel value="history">
            <ScrollArea h={450} mt="md">
              {historyEvents.length > 0 ? (
                <Timeline active={historyEvents.length} lineWidth={2}>
                  {historyEvents.map((event, index) => (
                    <Timeline.Item
                      key={index}
                      bullet={<IconHistory size={12} />}
                      title={event.action}
                    >
                      <Text c="dimmed" size="sm">
                        {event.details}
                      </Text>
                      <Group mt={4}>
                        <Text size="xs" c="dimmed">
                          By: {event.userId}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {event.timestamp
                            ? new Date(event.timestamp).toLocaleString()
                            : 'Unknown time'}
                        </Text>
                      </Group>
                    </Timeline.Item>
                  ))}
                </Timeline>
              ) : (
                <Text c="dimmed" size="sm">
                  No history events found. Audit trail data will appear here when available.
                </Text>
              )}
            </ScrollArea>
          </Tabs.Panel>
        </Tabs>
      )}
    </Modal>
  );
};

export default RelationshipModal;


